import React from 'react';
import { getBaseErrorHandler } from './getBaseErrorHandler';
import type {
    ErrorConfig,
    SimpleWrapperConfig,
    SuspenseLoadConfig,
    LazyLoadConfig,
    WrappedComponentType,
    SimpleComponent,
    SuspenseComponent,
} from '../schema/ErrorConfig';

/**
 * This HoC is to be used when we don't want to show any UI indication of failure while still logging the failure for tracking.
 * @description: Higher Order Component to take care of
 * 1. React Render Tree failures using error boundaries.
 * 2. Suspense load failure, when passed a lazy/suspense component.
 * 3. LazyComponent failure. Uses OWA bundling createLazyComponent component.
 */
export function withSilentErrorHandler<TProps>(
    WrappedComponent: SimpleComponent<TProps>,
    errorConfig: SimpleWrapperConfig
): React.ComponentType<TProps>;

export function withSilentErrorHandler<TProps>(
    WrappedComponent: SuspenseComponent<TProps>,
    errorConfig: SuspenseLoadConfig
): React.ComponentType<TProps>;

export function withSilentErrorHandler<TProps, TModule, TInnerRef, TPlaceHolderRef = void>(
    WrappedComponent: null,
    errorConfig: LazyLoadConfig<TProps, TModule, TInnerRef, TPlaceHolderRef>
): React.ComponentType<TProps>;

export function withSilentErrorHandler<TProps, TModule, TInnerRef, TPlaceHolderRef = void>(
    WrappedComponent: WrappedComponentType<TProps>,
    errorConfig: ErrorConfig<TProps, TModule, TInnerRef, TPlaceHolderRef>
): React.ComponentType<TProps> {
    return getBaseErrorHandler(SilentErrorScreen, WrappedComponent, errorConfig);
}

const SilentErrorScreen = () => null;
